/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import type { ErrorBody, ErrorMeta } from '@karya/http-response';
import { isHttpResponse } from '@karya/http-response';

/**
 * RTK Query error structure for fetchBaseQuery
 */
export type RTKQueryError = {
  status: number;
  data?: ErrorBody | null;
};

/**
 * Type guard to check if an error is an RTK Query error with ErrorBody format.
 *
 * Uses the http-response package's isHttpResponse as a base check,
 * then validates that the data matches ErrorBody structure.
 *
 * @param error - The error object to check
 * @returns True if the error matches RTKQueryError structure
 */
export function isRTKQueryError(error: unknown): error is RTKQueryError {
  if (!isHttpResponse(error)) {
    return false;
  }

  // Additional validation: ensure status is finite
  const err = error as { status: number; data?: unknown };
  return Number.isFinite(err.status);
}

/**
 * Type guard to check if an error has a valid ErrorBody data structure.
 *
 * Validates that:
 * - The error is an RTK Query error
 * - The data exists and is not null
 * - The data has a title property (required by ErrorBody)
 *
 * @param error - The error object to check
 * @returns True if the error has valid ErrorBody data
 */
export function hasErrorBody(error: unknown): error is RTKQueryError & { data: ErrorBody } {
  if (!isRTKQueryError(error) || error.data === null || error.data === undefined) {
    return false;
  }

  // Validate ErrorBody structure: must have title property
  const data = error.data as Record<string, unknown>;
  return typeof data.title === 'string';
}

/**
 * Extracts the ErrorBody from an RTK Query error.
 *
 * @param error - The error object
 * @returns The ErrorBody if present and valid, undefined otherwise
 */
export function getErrorBody(error: unknown): ErrorBody | undefined {
  if (hasErrorBody(error)) {
    return error.data;
  }
  return undefined;
}

/**
 * Extracts error messages from an ErrorBody.
 *
 * Filters out empty strings and ensures all messages are strings.
 *
 * @param errorBody - The ErrorBody to extract messages from
 * @returns Array of non-empty error messages, or empty array if none
 */
export function getErrorMessages(errorBody: ErrorBody): string[] {
  if (!Array.isArray(errorBody.messages)) {
    return [];
  }
  return errorBody.messages.filter((msg): msg is string => typeof msg === 'string' && msg.trim().length > 0);
}

/**
 * Extracts the error title from an ErrorBody.
 *
 * @param errorBody - The ErrorBody to extract title from
 * @returns The error title, or empty string if not present
 */
export function getErrorTitle(errorBody: ErrorBody): string {
  return typeof errorBody.title === 'string' ? errorBody.title : '';
}

/**
 * Extracts the ErrorMeta from an ErrorBody.
 *
 * Validates that the error meta has the required structure.
 *
 * @param errorBody - The ErrorBody to extract metadata from
 * @returns The ErrorMeta if present and valid, undefined otherwise
 */
export function getErrorMeta(errorBody: ErrorBody): ErrorMeta | undefined {
  const errorMeta = errorBody.error;
  if (!errorMeta || typeof errorMeta !== 'object') {
    return undefined;
  }

  // Validate ErrorMeta structure
  if (typeof errorMeta.code === 'string' && typeof errorMeta.context === 'object' && errorMeta.context !== null) {
    return errorMeta;
  }

  return undefined;
}

/**
 * Extracts a user-friendly error message from an unknown error object.
 *
 * Handles RTK Query errors that follow the ErrorBody format defined in
 * utils/http-response/src/Index.ts. Extracts messages from the error response
 * in the following priority:
 * 1. error.data.messages (array of strings, joined if multiple)
 * 2. error.data.title (fallback)
 * 3. Generic fallback message
 *
 * @param error - The error object to extract a message from
 * @returns A user-friendly error message string
 */
export function getErrorMessage(error: unknown): string {
  if (!error) {
    return 'Something went wrong. Please try again.';
  }

  const errorBody = getErrorBody(error);
  if (errorBody) {
    // Prefer messages array (join if multiple)
    const messages = getErrorMessages(errorBody);
    if (messages.length > 0) {
      return messages.join('. ');
    }

    // Fall back to title
    const title = getErrorTitle(errorBody);
    if (title) {
      return title;
    }
  }

  return 'Something went wrong. Please try again.';
}

/**
 * Extracts the HTTP status code from an RTK Query error.
 *
 * @param error - The error object
 * @returns The HTTP status code, or undefined if not an RTK Query error
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (isRTKQueryError(error)) {
    return error.status;
  }
  return undefined;
}

/**
 * Checks if an error is a client error (4xx status codes).
 *
 * @param error - The error object
 * @returns True if the error is a client error
 */
export function isClientError(error: unknown): boolean {
  const status = getErrorStatus(error);
  return status !== undefined && status >= 400 && status < 500;
}

/**
 * Checks if an error is a server error (5xx status codes).
 *
 * @param error - The error object
 * @returns True if the error is a server error
 */
export function isServerError(error: unknown): boolean {
  const status = getErrorStatus(error);
  return status !== undefined && status >= 500 && status < 600;
}

/**
 * Checks if an error is an authentication error (401 status code).
 *
 * @param error - The error object
 * @returns True if the error is an authentication error
 */
export function isAuthenticationError(error: unknown): boolean {
  return getErrorStatus(error) === 401;
}

/**
 * Checks if an error is an authorization error (403 status code).
 *
 * @param error - The error object
 * @returns True if the error is an authorization error
 */
export function isAuthorizationError(error: unknown): boolean {
  return getErrorStatus(error) === 403;
}

/**
 * Checks if an error is a not found error (404 status code).
 *
 * @param error - The error object
 * @returns True if the error is a not found error
 */
export function isNotFoundError(error: unknown): boolean {
  return getErrorStatus(error) === 404;
}

/**
 * Checks if an error is a conflict error (409 status code).
 *
 * @param error - The error object
 * @returns True if the error is a conflict error
 */
export function isConflictError(error: unknown): boolean {
  return getErrorStatus(error) === 409;
}

/**
 * Checks if an error is a rate limit error (429 status code).
 *
 * @param error - The error object
 * @returns True if the error is a rate limit error
 */
export function isRateLimitError(error: unknown): boolean {
  return getErrorStatus(error) === 429;
}

/**
 * Gets a comprehensive error information object from an error.
 *
 * @param error - The error object
 * @returns An object containing message, status, and metadata
 */
export function getErrorInfo(error: unknown): {
  message: string;
  status?: number;
  title?: string;
  messages?: string[];
  errorMeta?: ErrorMeta;
} {
  const message = getErrorMessage(error);
  const status = getErrorStatus(error);
  const errorBody = getErrorBody(error);

  return {
    message,
    status,
    title: errorBody ? getErrorTitle(errorBody) : undefined,
    messages: errorBody ? getErrorMessages(errorBody) : undefined,
    errorMeta: errorBody ? getErrorMeta(errorBody) : undefined,
  };
}
